import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import MarketingHeader from '../components/MarketingHeader'
import MarketingFooter from '../components/MarketingFooter'

const PER_PAGE = 12

// ── Search icon SVG ───────────────────────────────────────
function SearchIcon({ size = 18, color = '#6B6B6B' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7"/><line x1="17" y1="17" x2="22" y2="22"/>
    </svg>
  )
}

// ── Helpers ───────────────────────────────────────────────
function trunc(str, max) {
  if (!str || str.length <= max) return str || ''
  return str.slice(0, max).trimEnd() + '…'
}

function fmtDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

// Split "P1 & P2" → [p1, p2]; append surname if present
function splitNames(story) {
  const dn = story.display_name || ''
  const [p1 = '', p2 = ''] = dn.split(' & ')
  return { p1: p1.trim(), p2: p2.trim(), surname: (story.couple_surname || '').trim() }
}

// Short teaser line for the card
function getTagline(s, max = 95) {
  const src = s.relationship_story || s.his_how_met || s.her_how_met || s.looking_forward || ''
  if (!src) return ''
  // First sentence — no regex lookbehind (iOS Safari < 16.4 can't parse it).
  const m = src.match(/[.!?](\s|$)/)
  const firstSentence = m ? src.slice(0, m.index + 1) : src
  return trunc(firstSentence, max)
}

// ── Budget-style line icons (stroke #8C7B70, strokeWidth 1.5) ──
function Icon({ d, children, size = 15, color = '#9B8E84' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {children || <path d={d}/>}
    </svg>
  )
}
const CalendarIcon = (p) => <Icon {...p}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Icon>
const PinIcon      = (p) => <Icon {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></Icon>
const UsersIcon    = (p) => <Icon {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></Icon>
const AsoEbiIcon   = (p) => <Icon {...p}><path d="M20.38 3.46L16 2l-1.5 3.5-3-3.5-3 3.5L7 2 2.62 3.46a1 1 0 00-.64 1.28L6 20h12l4.02-15.26a1 1 0 00-.64-1.28z"/></Icon>

// ── Meta row (date / location / guests / aso ebi) ─────────
function MetaRow({ story, gap = '0.55rem 1rem', fontSize = '0.74rem' }) {
  const item = (IconC, label) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.32rem', fontFamily: 'Nunito Sans, sans-serif', fontSize, color: '#6B6B6B' }}>
      {IconC}{label}
    </span>
  )
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap }}>
      {story.wedding_date && item(<CalendarIcon/>, fmtDate(story.wedding_date))}
      {story.city         && item(<PinIcon/>, story.city)}
      {story.guest_count > 0  && item(<UsersIcon/>, `${story.guest_count} guests`)}
      {story.asoebi_count > 0 && item(<AsoEbiIcon/>, `${story.asoebi_count} Aso Ebi`)}
    </div>
  )
}

// ── Couple name with heart ────────────────────────────────
function CoupleName({ story, fontSize }) {
  const { p1, p2, surname } = splitNames(story)
  return (
    <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize, color: '#1A1A1A', margin: 0, lineHeight: 1.3, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
      {p1}
      {p1 && p2 && <span style={{ color: '#C4826A', margin: '0 0.25rem' }}>&amp;</span>}
      {p2 || (p1 ? '' : story.display_name)}
      {surname && <span> {surname}</span>}
    </h2>
  )
}

// ── Story card (masonry tile) ─────────────────────────────
function StoryCard({ story, onOpen }) {
  const photos = story.photos || []
  const cover  = story.story_cover_url || photos[0]
  const tagline = getTagline(story.story || {})

  return (
    <div
      onClick={onOpen}
      style={{ background: 'white', border: '1px solid #E5E0D8', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', boxShadow: '0 1px 3px rgba(28,20,16,0.04)', transition: 'transform 0.22s, box-shadow 0.22s' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(28,20,16,0.12)' }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(28,20,16,0.04)' }}
    >
      {/* Cover — natural aspect ratio drives the masonry */}
      {cover ? (
        <img src={cover} alt="" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.style.display = 'none' }}/>
      ) : (
        <div style={{ aspectRatio: '4 / 3', background: '#F3F0EA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontFamily: 'Gibs, serif', fontSize: '2rem', color: 'rgba(196,130,106,0.35)' }}>O</span>
        </div>
      )}

      {/* Body */}
      <div className="story-card-body" style={{ padding: '1.05rem 1.1rem 1.15rem' }}>
        <div className="story-card-name" style={{ fontSize: '1.3rem' }}>
          <CoupleName story={story} fontSize="inherit"/>
        </div>
        {tagline && (
          <p className="story-card-tagline" style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.85rem', color: '#6B6B6B', lineHeight: 1.6, margin: '0.4rem 0 0.85rem', fontStyle: 'italic' }}>
            "{tagline}"
          </p>
        )}
        <div className="story-card-meta">
          <MetaRow story={story}/>
        </div>
      </div>
    </div>
  )
}

// ── Modal photo arrangement (up to 3) ─────────────────────
function ModalPhotos({ photos, cover }) {
  // Cover first, then up to two others, de-duped
  const ordered = [cover, ...photos.filter(p => p !== cover)].filter(Boolean).slice(0, 3)
  const img = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }

  if (ordered.length === 0) {
    return <div style={{ height: 220, background: '#F3F0EA' }}/>
  }
  if (ordered.length === 1) {
    return <div style={{ maxHeight: 360, overflow: 'hidden' }}><img src={ordered[0]} alt="" style={{ width: '100%', height: 'auto', display: 'block' }}/></div>
  }
  if (ordered.length === 2) {
    return (
      <div style={{ display: 'flex', gap: 3, height: 300 }}>
        {ordered.map((u, i) => <div key={i} style={{ flex: 1, overflow: 'hidden' }}><img src={u} alt="" style={img}/></div>)}
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', gap: 3, height: 320 }}>
      <div style={{ flex: '0 0 62%', overflow: 'hidden' }}><img src={ordered[0]} alt="" style={img}/></div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {ordered.slice(1).map((u, i) => <div key={i} style={{ flex: 1, overflow: 'hidden' }}><img src={u} alt="" style={img}/></div>)}
      </div>
    </div>
  )
}

// ── Story section in modal ────────────────────────────────
function StorySection({ label, text }) {
  if (!text || text.trim().length < 20) return null
  return (
    <div style={{ marginBottom: '1.6rem' }}>
      <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.66rem', fontWeight: 700, color: '#C4826A', textTransform: 'uppercase', letterSpacing: '0.14em', margin: '0 0 0.45rem' }}>{label}</p>
      <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.92rem', color: '#1A1A1A', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap' }}>{text}</p>
    </div>
  )
}

// ── Modal ─────────────────────────────────────────────────
function StoryModal({ story, onClose }) {
  const s = story.story || {}
  const photos = story.photos || []
  const cover  = story.story_cover_url || photos[0]
  const { p1, p2 } = splitNames(story)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey) }
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(28,20,16,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '3vh 1rem', overflowY: 'auto' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="story-modal"
        style={{ background: 'white', borderRadius: 18, width: '100%', maxWidth: 620, maxHeight: '94vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.4)', position: 'relative' }}
      >
        {/* Close */}
        <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 12, right: 12, zIndex: 2, width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.92)', color: '#1A1A1A', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>✕</button>

        {/* Photos */}
        <ModalPhotos photos={photos} cover={cover}/>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', padding: '1.5rem 1.6rem 0' }}>
          {/* Names */}
          <CoupleName story={story} fontSize="1.7rem"/>

          {/* Detail strip */}
          <div style={{ margin: '0.9rem 0 1.4rem', paddingBottom: '1.4rem', borderBottom: '1px solid #EEE9E2' }}>
            <MetaRow story={story} fontSize="0.8rem" gap="0.55rem 1.25rem"/>
          </div>

          {/* Story */}
          <StorySection label="How they met"   text={s.his_how_met}/>
          <StorySection label={p2 ? `${p2}'s side` : 'Her side'} text={s.her_how_met}/>
          <StorySection label="Their story"    text={s.relationship_story}/>
          <StorySection label="The proposal"   text={s.his_proposal}/>
          <StorySection label={p2 ? `${p2} remembers` : 'Her side'} text={s.her_proposal}/>
          <StorySection label={p1 ? `Why ${p1} chose ${p2 || 'her'}` : 'In his words'} text={s.his_why}/>
          <StorySection label={p2 ? `Why ${p2} chose ${p1 || 'him'}` : 'In her words'} text={s.her_why}/>
          <StorySection label="Looking forward" text={s.looking_forward}/>
        </div>

        {/* Footer — Wrapped */}
        {story.has_wrapped && story.slug && (
          <div style={{ padding: '1rem 1.6rem 1.4rem', borderTop: '1px solid #EEE9E2', background: 'white' }}>
            <a href={`/wrapped/${story.slug}`} target="_blank" rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: 'white', background: '#C4826A', textDecoration: 'none', borderRadius: 100, padding: '0.85rem 1.5rem' }}>
              Watch their Wrapped →
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────
function SkeletonCard({ h }) {
  return (
    <div style={{ background: 'white', border: '1px solid #E5E0D8', borderRadius: 14, overflow: 'hidden', marginBottom: 18 }}>
      <div className="sk-shimmer" style={{ height: h }}/>
      <div style={{ padding: '1.05rem 1.1rem 1.15rem' }}>
        <div className="sk-shimmer" style={{ height: 22, width: '60%', borderRadius: 8, marginBottom: 12 }}/>
        <div className="sk-shimmer" style={{ height: 12, width: '90%', borderRadius: 6, marginBottom: 14 }}/>
        <div className="sk-shimmer" style={{ height: 12, width: '70%', borderRadius: 6 }}/>
      </div>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────
function EmptyState() {
  return (
    <div style={{ textAlign: 'center', padding: '5rem 1rem 4rem' }}>
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ marginBottom: '1.5rem' }}>
        <circle cx="24" cy="24" r="22" stroke="#C4826A" strokeWidth="2" fill="none"/>
        <text x="24" y="30" textAnchor="middle" style={{ fontFamily: 'Gibs, serif', fontSize: 18 }} fill="#C4826A">O</text>
      </svg>
      <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.5rem', color: '#1A1A1A', margin: '0 0 1rem' }}>
        The first story is being written.
      </h2>
      <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.875rem', color: '#6B6B6B', lineHeight: 1.75, maxWidth: 360, margin: '0 auto 2rem' }}>
        Real couples are planning their weddings on Okoiya right now. When their days arrive — and they
        share their stories — they'll live here.
      </p>
      <Link to="/signup">
        <button style={{ fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '0.875rem', color: '#6B6B6B', background: 'white', border: '1.5px solid #D1C9C0', borderRadius: 100, padding: '0.75rem 1.75rem', cursor: 'pointer' }}>
          Start your own story →
        </button>
      </Link>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────
import { useSeo } from '../lib/seo'

export default function StoriesPage() {
  useSeo({ title: 'Real Wedding Stories', description: 'Love stories and weddings from couples planning with Okoiya — how they met, and the day they said "I do".', path: '/stories' })
  const [stories, setStories] = useState(null) // null = loading
  const [page, setPage] = useState(0)
  const [active, setActive] = useState(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [query, setQuery] = useState('')

  useEffect(() => {
    supabase.rpc('get_featured_stories')
      .then(({ data, error }) => {
        if (error) {
          console.error('[StoriesPage] RPC error — check SQL was run:', error.message)
          setStories([])
          return
        }
        const rows = Array.isArray(data) ? data : (data ? [data] : [])
        setStories(rows)
      })
  }, [])

  const loading = stories === null
  const q = query.trim().toLowerCase()
  const filtered = q
    ? (stories || []).filter(s => {
        const name = (s.display_name || '').toLowerCase()
        const surname = (s.couple_surname || '').toLowerCase()
        return name.includes(q) || surname.includes(q)
      })
    : (stories || [])
  const total = filtered.length
  const pageCount = Math.ceil(total / PER_PAGE)
  const pageStories = filtered.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  function goTo(p) {
    setPage(p)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function handleSearch(val) {
    setQuery(val)
    setPage(0)
  }

  return (
    <>
      <style>{`
        @keyframes sk-shimmer { 0% { background-position: -400px 0 } 100% { background-position: 400px 0 } }
        .sk-shimmer {
          background: linear-gradient(90deg, #F0EDE8 25%, #E8E4DE 50%, #F0EDE8 75%);
          background-size: 800px 100%;
          animation: sk-shimmer 1.6s infinite linear;
        }
        .stories-masonry { column-count: 3; column-gap: 22px; }
        .stories-masonry > * { break-inside: avoid; margin-bottom: 22px; }
        @media (max-width: 980px) { .stories-masonry { column-count: 2; column-gap: 18px; } .stories-masonry > * { margin-bottom: 18px; } }
        @media (max-width: 600px) {
          .stories-masonry { column-count: 2; column-gap: 10px; }
          .stories-masonry > * { margin-bottom: 10px; }
          .story-card-body { padding: 0.65rem 0.7rem 0.75rem !important; }
          .story-card-name { font-size: 0.8rem !important; }
          .story-card-tagline { display: none !important; }
          .story-card-meta span { font-size: 0.6rem !important; }
        }
      `}</style>

      <MarketingHeader/>

      {/* Masthead */}
      <section style={{ background: '#FAFAF8', textAlign: 'center', padding: '100px 1.5rem 56px', position: 'relative' }}>
        {/* Search toggle — top right of masthead */}
        <div style={{ position: 'absolute', top: 112, right: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {searchOpen && (
            <input
              autoFocus
              value={query}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search couples…"
              style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.85rem', color: '#1A1A1A', background: 'white', border: '1.5px solid #E5E0D8', borderRadius: 100, padding: '0.45rem 1rem', outline: 'none', width: 190, transition: 'border-color 0.15s' }}
              onFocus={e => e.target.style.borderColor = '#C4826A'}
              onBlur={e => e.target.style.borderColor = '#E5E0D8'}
            />
          )}
          <button
            onClick={() => { setSearchOpen(o => !o); if (searchOpen) handleSearch('') }}
            aria-label={searchOpen ? 'Close search' : 'Search stories'}
            style={{ width: 38, height: 38, borderRadius: '50%', border: '1.5px solid #E5E0D8', background: searchOpen ? '#C4826A' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'background 0.15s, border-color 0.15s' }}
          >
            <SearchIcon size={16} color={searchOpen ? 'white' : '#6B6B6B'}/>
          </button>
        </div>

        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.68rem', fontWeight: 700, color: '#C4826A', letterSpacing: '0.2em', textTransform: 'uppercase', margin: '0 0 1.5rem' }}>
          REAL COUPLES · REAL WEDDINGS
        </p>
        <h1 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, color: '#1A1A1A', fontSize: 'clamp(2.25rem, 5vw, 3.25rem)', lineHeight: 1.15, maxWidth: 640, margin: '0 auto 1.25rem', letterSpacing: '-0.02em' }}>
          Love Stories<br/>from <span style={{ color: '#C4826A' }}>Okoiya</span>
        </h1>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '1rem', color: '#6B6B6B', maxWidth: 480, margin: '0 auto', lineHeight: 1.75 }}>
          Every couple below planned their wedding on Okoiya. Tap any story to read it in their own words.
        </p>

        {/* Search results hint */}
        {q && (
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.82rem', color: '#9B8E84', margin: '1.5rem auto 0', maxWidth: 480 }}>
            {total === 0 ? `No stories found for "${query}"` : `${total} stor${total === 1 ? 'y' : 'ies'} matching "${query}"`}
          </p>
        )}
      </section>

      {/* Grid */}
      <section style={{ maxWidth: 1140, margin: '0 auto', padding: '2.5rem 1.5rem 0' }}>
        {loading ? (
          <div className="stories-masonry">
            {[300, 380, 260, 340, 300, 360].map((h, i) => <SkeletonCard key={i} h={h}/>)}
          </div>
        ) : total === 0 ? (
          <EmptyState/>
        ) : (
          <>
            <div className="stories-masonry">
              {pageStories.map(s => (
                <StoryCard key={s.id} story={s} onOpen={() => setActive(s)}/>
              ))}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '3rem 0 0' }}>
                <button onClick={() => goTo(page - 1)} disabled={page === 0}
                  style={{ fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '0.85rem', color: page === 0 ? '#C9C2B9' : '#1A1A1A', background: 'white', border: '1.5px solid #E5E0D8', borderRadius: 100, padding: '0.6rem 1.3rem', cursor: page === 0 ? 'default' : 'pointer' }}>
                  ← Prev
                </button>
                <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.82rem', color: '#6B6B6B' }}>
                  Page {page + 1} of {pageCount}
                </span>
                <button onClick={() => goTo(page + 1)} disabled={page >= pageCount - 1}
                  style={{ fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '0.85rem', color: page >= pageCount - 1 ? '#C9C2B9' : '#1A1A1A', background: 'white', border: '1.5px solid #E5E0D8', borderRadius: 100, padding: '0.6rem 1.3rem', cursor: page >= pageCount - 1 ? 'default' : 'pointer' }}>
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Footer nudge */}
      {!loading && total > 0 && (
        <section style={{ textAlign: 'center', padding: '72px 1.5rem 5rem' }}>
          <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.25rem', color: '#1A1A1A', margin: '0 0 0.875rem' }}>
            Your story could live here too.
          </h2>
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.875rem', color: '#6B6B6B', lineHeight: 1.75, maxWidth: 420, margin: '0 auto 2rem' }}>
            Plan your wedding on Okoiya — and one day, with your permission, we'd love to share it.
          </p>
          <Link to="/signup">
            <button style={{ fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '0.875rem', color: 'white', background: '#C4826A', border: 'none', borderRadius: 100, padding: '0.875rem 2rem', cursor: 'pointer', transition: 'background 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#B36B55'}
              onMouseLeave={e => e.currentTarget.style.background = '#C4826A'}>
              Start planning free →
            </button>
          </Link>
        </section>
      )}

      <MarketingFooter/>

      {active && <StoryModal story={active} onClose={() => setActive(null)}/>}
    </>
  )
}
